// frontend/src/pages/RealtorDocumentsPage.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Grid, Button, Card, CardContent, Alert, CircularProgress, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, LinearProgress
} from '@mui/material';
import { ArrowBack, CloudUpload, Delete, Visibility, CheckCircle, AccessTime, Error as ErrorIcon } from '@mui/icons-material';
import realtorService from '../services/realtorService';
import { brand, status as statusColors, text, shadow } from '../theme/colors';

// value = what we send as documentType; matches = user_documents.document_type values it satisfies
const DOCUMENT_TYPES = [
  { value: 'national_id', label: 'National ID or passport', required: true, matches: ['id_card', 'passport', 'license'] },
  { value: 'eac_practising_certificate', label: 'EAC practising certificate (current year)', required: true, matches: ['eac_practising_certificate'] },
  { value: 'agency_engagement_letter', label: 'Agency letter of engagement', required: false, matches: ['agency_engagement_letter'] },
];

const STATUS = {
  approved: { color: statusColors.success, bg: statusColors.successBg, icon: <CheckCircle fontSize="small" />, label: 'Verified' },
  pending: { color: statusColors.warning, bg: statusColors.warningBg, icon: <AccessTime fontSize="small" />, label: 'Pending review' },
  flagged: { color: statusColors.warning, bg: statusColors.warningBg, icon: <AccessTime fontSize="small" />, label: 'Under review' },
  rejected: { color: statusColors.error, bg: statusColors.errorBg, icon: <ErrorIcon fontSize="small" />, label: 'Rejected — re-upload' },
};

const RealtorDocumentsPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({ documentType: '', file: null });
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await realtorService.listDocuments();
      setDocuments(res.data || []);
    } catch { setError('Failed to load documents'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!form.file || !form.documentType) { setError('Choose a document type and a file'); return; }
    if (form.file.size > 10 * 1024 * 1024) { setError('Files must be under 10 MB'); return; }
    try {
      setUploading(true); setError('');
      await realtorService.uploadDocument(form.file, form.documentType, setProgress);
      setSuccess('Document uploaded');
      setOpen(false); setForm({ documentType: '', file: null });
      load();
    } catch (err) { setError(err.response?.data?.message || err.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); setProgress(0); }
  };

  const handleDelete = async () => {
    try {
      await realtorService.deleteDocument(deleting.id);
      setSuccess('Document deleted'); setDeleting(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const docsFor = (t) => documents.filter((d) => t.matches.includes(d.document_type));
  const requiredMissing = DOCUMENT_TYPES.filter((t) => t.required && docsFor(t).every((d) => d.verification_status === 'rejected') );
  const requiredCount = DOCUMENT_TYPES.filter((t) => t.required).length;
  const completion = Math.round(((requiredCount - requiredMissing.length) / requiredCount) * 100);

  if (loading) return <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: brand.primary }} /></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ color: text.secondary }}><ArrowBack /></IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary }}>Verification documents</Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>Your ID and EAC practising certificate are reviewed by our team</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setOpen(true)}
          sx={{ background: brand.gradient, fontWeight: 600, boxShadow: shadow.md, '&:hover': { background: brand.gradientHover } }}>
          Upload document
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Required documents uploaded: {completion}%</Typography>
        <LinearProgress variant="determinate" value={completion} sx={{ height: 8, borderRadius: 4 }} />
        {requiredMissing.length > 0 && (
          <Typography variant="body2" sx={{ color: text.secondary, mt: 1 }}>
            Still needed: {requiredMissing.map((t) => t.label).join(', ')}
          </Typography>
        )}
      </Paper>

      <Grid container spacing={2}>
        {DOCUMENT_TYPES.map((t) => (
          <Grid item xs={12} md={4} key={t.value}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography sx={{ fontWeight: 600 }}>{t.label}</Typography>
                <Chip size="small" label={t.required ? 'Required' : 'Optional'} sx={{ mt: 0.5, mb: 1.5 }} />
                {docsFor(t).length === 0 && <Typography variant="body2" sx={{ color: text.tertiary }}>Not uploaded yet</Typography>}
                {docsFor(t).map((d) => {
                  const s = STATUS[d.verification_status] || STATUS.pending;
                  return (
                    <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip size="small" icon={s.icon} label={s.label} sx={{ color: s.color, bgcolor: s.bg }} />
                      <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.document_name}</Typography>
                      <IconButton size="small" component="a" href={d.file_url} target="_blank" rel="noopener noreferrer"><Visibility fontSize="small" /></IconButton>
                      {d.verification_status !== 'approved' && (
                        <IconButton size="small" onClick={() => setDeleting(d)}><Delete fontSize="small" /></IconButton>
                      )}
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={() => !uploading && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload document</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Document type" value={form.documentType} sx={{ mt: 1, mb: 2 }}
            onChange={(e) => setForm({ ...form, documentType: e.target.value })}>
            {DOCUMENT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <Button variant="outlined" component="label" fullWidth>
            {form.file ? form.file.name : 'Choose a PDF or image (max 10 MB)'}
            <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
          </Button>
          {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={uploading || !form.file || !form.documentType}>Upload</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete document?</DialogTitle>
        <DialogContent><Typography>{deleting?.document_name} will be removed. You can upload a replacement afterwards.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RealtorDocumentsPage;
